/**
 * This code was created with AI assistance to implement smooth, natural scrolling animations
 * with organic timing variations and proper end-of-scroll boundary handling.
 */

import { checkScrollVisibility } from '../../utils';

export const smoothScrollTo = (element: HTMLElement, targetScrollTop: number, duration: number = 800) => {
	const startScrollTop = element.scrollTop;
	const maxScrollTop = element.scrollHeight - element.clientHeight;

	// Clamp the target to valid scroll bounds
	const clampedTarget = Math.max(0, Math.min(targetScrollTop, maxScrollTop));
	const distance = clampedTarget - startScrollTop;

	// If there's no distance to scroll, don't animate
	if (Math.abs(distance) < 1) {
		return;
	}

	const startTime = performance.now();
	const easeOutCubic = (t: number): number => 1 - (1 - t) ** 3;

	const animateScroll = (currentTime: number) => {
		const timeElapsed = currentTime - startTime;
		const progress = Math.min(timeElapsed / duration, 1);

		const easedProgress = easeOutCubic(progress);
		const newScrollTop = startScrollTop + distance * easedProgress;

		// Ensure we don't scroll beyond bounds during animation
		// oxlint-disable-next-line no-param-reassign
		element.scrollTop = Math.max(0, Math.min(newScrollTop, maxScrollTop));

		if (progress < 1) {
			requestAnimationFrame(animateScroll);
		}
	};

	requestAnimationFrame(animateScroll);
};

export const getRandomInterval = (baseSeconds: number) => {
	const shouldPause = Math.random() < 0.15; // 15% chance for longer pause
	const multiplier = shouldPause ? 1.8 : 1;
	const variation = baseSeconds * 0.2; // 20% variation
	const randomOffset = (Math.random() - 0.5) * 2 * variation;
	return (baseSeconds * multiplier + randomOffset) * 1000;
};

export const calculateScrollDistance = (scroller: HTMLElement) => {
	const children = Array.from(scroller.children);
	const firstChild = children[0];
	const secondChild = children[1];

	// Calculate actual distance dynamically by measuring the space between elements
	// This accounts for browser-specific rendering differences (Chrome vs Safari)
	if (firstChild instanceof HTMLElement && secondChild instanceof HTMLElement) {
		// The distance from the top of first element to the top of second element
		// This includes the element height + actual gap (no hardcoding)
		const actualDistance = secondChild.offsetTop - firstChild.offsetTop;
		return actualDistance;
	}

	// Fallback if we can't measure (shouldn't happen in practice)
	const itemHeight = firstChild instanceof HTMLElement ? firstChild.offsetHeight : 0;
	return itemHeight + 8; // fallback gap
};

export const isAtScrollEnd = (scroller: HTMLElement, last: HTMLElement | null) => {
	const maxScrollTop = scroller.scrollHeight - scroller.clientHeight;
	const isAtBottom = scroller.scrollTop >= maxScrollTop - 1; // Allow 1px tolerance
	const lastElementFullyVisible = last && checkScrollVisibility(scroller, last).bottom;
	return lastElementFullyVisible || isAtBottom;
};
